import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Row, Col, Card, CardBody, CardHeader,
  Button, Badge, Input, Label, FormGroup, Alert,
  Spinner, Modal, ModalHeader, ModalBody, ModalFooter,
} from 'reactstrap';
import { getAlerts, createAlert, updateAlert, deleteAlert } from '../actions/alerts';
import { usePostHog } from '@posthog/react';

// ─── Constants ────────────────────────────────────────────────────────────────

const WINDOW_OPTIONS: { value: WindowName; label: string }[] = [
  { value: '2w', label: '2 weeks' },
  { value: '4w', label: '4 weeks' },
  { value: '3m', label: '3 months' },
  { value: '6m', label: '6 months' },
  { value: '1y', label: '1 year' },
];

const PATTERN_WINDOW_OPTIONS: { value: number; label: string }[] = [
  { value: 22,  label: '1 month' },
  { value: 45,  label: '2 months' },
  { value: 65,  label: '3 months' },
  { value: 130, label: '6 months' },
  { value: 195, label: '9 months' },
  { value: 260, label: '1 year' },
];

const PATTERN_OPTIONS: { value: string; label: string; sentiment: 'bullish' | 'bearish' }[] = [
  { value: 'cup_handle', label: 'Cup & Handle', sentiment: 'bullish' },
  { value: 'inv_head_shoulders', label: 'Inverse H&S', sentiment: 'bullish' },
  { value: 'double_bottom', label: 'Double Bottom', sentiment: 'bullish' },
  { value: 'triple_bottom', label: 'Triple Bottom', sentiment: 'bullish' },
  { value: 'rounding_bottom', label: 'Rounding Bottom', sentiment: 'bullish' },
  { value: 'ascending_triangle', label: 'Ascending △', sentiment: 'bullish' },
  { value: 'falling_wedge', label: 'Falling Wedge', sentiment: 'bullish' },
  { value: 'bull_flag', label: 'Bull Flag', sentiment: 'bullish' },
  { value: 'bull_pennant', label: 'Bull Pennant', sentiment: 'bullish' },
  { value: 'rectangle_bull', label: 'Rectangle (Bull)', sentiment: 'bullish' },
  { value: 'symmetrical_triangle', label: 'Symmetrical △', sentiment: 'bullish' },
  { value: 'head_shoulders', label: 'Head & Shoulders', sentiment: 'bearish' },
  { value: 'double_top', label: 'Double Top', sentiment: 'bearish' },
  { value: 'triple_top', label: 'Triple Top', sentiment: 'bearish' },
  { value: 'rounding_top', label: 'Rounding Top', sentiment: 'bearish' },
  { value: 'descending_triangle', label: 'Descending △', sentiment: 'bearish' },
  { value: 'rising_wedge', label: 'Rising Wedge', sentiment: 'bearish' },
  { value: 'bear_flag', label: 'Bear Flag', sentiment: 'bearish' },
  { value: 'bear_pennant', label: 'Bear Pennant', sentiment: 'bearish' },
  { value: 'rectangle_bear', label: 'Rectangle (Bear)', sentiment: 'bearish' },
  { value: 'inv_cup_handle', label: 'Inv Cup & Handle', sentiment: 'bearish' },
  { value: 'broadening_formation', label: 'Broadening', sentiment: 'bearish' },
];

// ─── Indicator filter editor ──────────────────────────────────────────────────

function IndicatorFilterEditor({
  filter, onChange, onRemove,
}: { filter: IndicatorFilter; onChange: (u: IndicatorFilter) => void; onRemove: () => void }) {
  const { type } = filter;
  return (
    <div className="d-flex gap-2 align-items-start mb-2 p-2 border rounded small">
      <div className="flex-grow-1">
        <FormGroup className="mb-1">
          <Input type="select" bsSize="sm" value={type} onChange={(e) => {
            const t = e.target.value as IndicatorFilter['type'];
            if (t === 'rsi') onChange({ type: 'rsi' });
            else if (t === 'macd') onChange({ type: 'macd', signal: 'bullish_cross' });
            else if (t === 'bbands') onChange({ type: 'bbands', position: 'above_upper' });
            else onChange({ type: 'stoch' });
          }}>
            <option value="rsi">RSI</option>
            <option value="macd">MACD</option>
            <option value="bbands">Bollinger Bands</option>
            <option value="stoch">Stochastic %K</option>
          </Input>
        </FormGroup>
        {type === 'rsi' && (
          <Row className="g-1">
            <Col><Input type="number" bsSize="sm" placeholder="Min RSI"
              value={(filter as { type: 'rsi'; min?: number }).min ?? ''}
              onChange={(e) => onChange({ ...filter, min: e.target.value ? +e.target.value : undefined } as IndicatorFilter)} /></Col>
            <Col xs="auto" className="d-flex align-items-center">–</Col>
            <Col><Input type="number" bsSize="sm" placeholder="Max RSI"
              value={(filter as { type: 'rsi'; max?: number }).max ?? ''}
              onChange={(e) => onChange({ ...filter, max: e.target.value ? +e.target.value : undefined } as IndicatorFilter)} /></Col>
          </Row>
        )}
        {type === 'macd' && (
          <Input type="select" bsSize="sm" value={(filter as { type: 'macd'; signal: string }).signal}
            onChange={(e) => onChange({ type: 'macd', signal: e.target.value as 'bullish_cross' | 'bearish_cross' })}>
            <option value="bullish_cross">Bullish cross (MACD crosses above signal)</option>
            <option value="bearish_cross">Bearish cross (MACD crosses below signal)</option>
          </Input>
        )}
        {type === 'bbands' && (
          <Input type="select" bsSize="sm" value={(filter as { type: 'bbands'; position: string }).position}
            onChange={(e) => onChange({ type: 'bbands', position: e.target.value as 'above_upper' | 'below_lower' | 'inside' })}>
            <option value="above_upper">Price above upper band</option>
            <option value="below_lower">Price below lower band</option>
            <option value="inside">Price inside bands</option>
          </Input>
        )}
        {type === 'stoch' && (
          <Row className="g-1">
            <Col><Input type="number" bsSize="sm" placeholder="Min %K"
              value={(filter as { type: 'stoch'; min_k?: number }).min_k ?? ''}
              onChange={(e) => onChange({ ...filter, min_k: e.target.value ? +e.target.value : undefined } as IndicatorFilter)} /></Col>
            <Col xs="auto" className="d-flex align-items-center">–</Col>
            <Col><Input type="number" bsSize="sm" placeholder="Max %K"
              value={(filter as { type: 'stoch'; max_k?: number }).max_k ?? ''}
              onChange={(e) => onChange({ ...filter, max_k: e.target.value ? +e.target.value : undefined } as IndicatorFilter)} /></Col>
          </Row>
        )}
      </div>
      <Button size="sm" color="link" className="text-danger p-0 ms-1" onClick={onRemove}>✕</Button>
    </div>
  );
}

// ─── Alert editor modal ───────────────────────────────────────────────────────

interface AlertEditorProps {
  alert: UserAlert | null;
  onSave: (data: Omit<UserAlert, 'id' | 'created_at' | 'updated_at'>) => void;
  onClose: () => void;
}

function AlertEditor({ alert, onSave, onClose }: AlertEditorProps) {
  const existingFilters = alert?.filters ?? {};
  const [name, setName] = useState(alert?.name ?? '');
  const [active, setActive] = useState(alert?.active ?? true);
  const [watchlistOnly, setWatchlistOnly] = useState(!!existingFilters.watchlist_only);
  const [frequency, setFrequency] = useState<'daily' | 'weekly'>(alert?.frequency ?? 'daily');

  const [hasFM, setHasFM] = useState('featured_match' in existingFilters || alert === null);
  const [hasCP, setHasCP] = useState('classic_pattern' in existingFilters);
  const [hasInd, setHasInd] = useState('indicators' in existingFilters);
  const [hasMR, setHasMR] = useState('momentum_rank' in existingFilters);

  const existingMR = (existingFilters.momentum_rank ?? {}) as MomentumRankFilters;
  const [mrMinRank, setMrMinRank] = useState<string>(existingMR.min_upr_rank?.toString() ?? '');
  const [mrMinRankChg, setMrMinRankChg] = useState<string>(existingMR.min_upr_rank_chg?.toString() ?? '');
  // existingFilters.featured_match is undefined for NEW alerts — the previous
  // unguarded casts here crashed the editor on open ("can't access property
  // min_avg_up"). Guard with ?? {} like every other section does.
  const existingFM = (existingFilters.featured_match ?? {}) as FeaturedMatchFilters;
  const [fmAdvancedOpen, setFmAdvancedOpen] = useState(
    !!existingFM.min_avg_up || !!existingFM.min_fraction_up || !!existingFM.min_count_total
  );
  const [fmWindow, setFmWindow] = useState<WindowName>((existingFM.window_name as WindowName | undefined) ?? '2w');
  const [fmDirection, setFmDirection] = useState<'up' | 'down'>(existingFM.direction ?? 'up');
  const [fmMinUoD, setFmMinUoD] = useState<string>(existingFM.min_up_over_down?.toString() ?? '1.5');
  const [fmMinAvgUp, setFmMinAvgUp] = useState<string>(existingFM.min_avg_up?.toString() ?? '');
  const [fmMinFrac, setFmMinFrac] = useState<string>(
    existingFM.min_fraction_up !== undefined ? (existingFM.min_fraction_up * 100).toString() : ''
  );
  const [fmMinCount, setFmMinCount] = useState<string>(existingFM.min_count_total?.toString() ?? '');

  const existingCP = (existingFilters.classic_pattern ?? {}) as ClassicPatternFilters;
  const [selectedPatternTypes, setSelectedPatternTypes] = useState<Set<string>>(
    new Set(existingCP.pattern_types ?? [])
  );
  const [cpWindows, setCpWindows] = useState<Set<number>>(
    new Set(existingCP.windows ?? [])
  );
  const [cpSentiment, setCpSentiment] = useState<'bullish' | 'bearish' | 'all'>(
    existingCP.sentiment ?? 'all'
  );

  const [indicators, setIndicators] = useState<IndicatorFilter[]>(existingFilters.indicators ?? []);

  const togglePatternType = (pt: string) => {
    const next = new Set(selectedPatternTypes);
    if (next.has(pt)) next.delete(pt);
    else next.add(pt);
    setSelectedPatternTypes(next);
  };

  const toggleCpWindow = (w: number) => {
    const next = new Set(cpWindows);
    if (next.has(w)) next.delete(w);
    else next.add(w);
    setCpWindows(next);
  };

  const buildFilters = (): AlertFilters => {
    const filters: AlertFilters = {};
    if (hasFM) {
      filters.featured_match = {
        window_name: fmWindow,
        direction: fmDirection,
        min_up_over_down: fmMinUoD ? parseFloat(fmMinUoD) : 1.0,
        ...(fmMinAvgUp ? { min_avg_up: parseFloat(fmMinAvgUp) } : {}),
        ...(fmMinFrac ? { min_fraction_up: parseFloat(fmMinFrac) / 100 } : {}),
        ...(fmMinCount ? { min_count_total: parseInt(fmMinCount, 10) } : {}),
      };
    }
    if (hasCP) {
      filters.classic_pattern = {
        ...(selectedPatternTypes.size > 0 ? { pattern_types: [...selectedPatternTypes] } : {}),
        ...(cpWindows.size > 0 ? { windows: [...cpWindows].sort((a, b) => a - b) } : {}),
        ...(cpSentiment !== 'all' ? { sentiment: cpSentiment } : {}),
      };
    }
    if (hasInd && indicators.length > 0) {
      filters.indicators = indicators;
    }
    if (hasMR) {
      const mr: MomentumRankFilters = {};
      if (mrMinRank) mr.min_upr_rank = parseFloat(mrMinRank);
      if (mrMinRankChg) mr.min_upr_rank_chg = parseFloat(mrMinRankChg);
      filters.momentum_rank = mr;
    }
    if (watchlistOnly) {
      filters.watchlist_only = true;
    }
    return filters;
  };

  const canSave = name.trim() !== '' && (hasFM || hasCP || hasInd || hasMR);

  const handleSave = () => {
    onSave({ name, active, filters: buildFilters(), frequency });
  };

  const bullishPatterns = PATTERN_OPTIONS.filter((p) => p.sentiment === 'bullish');
  const bearishPatterns = PATTERN_OPTIONS.filter((p) => p.sentiment === 'bearish');

  return (
    <>
      <ModalBody style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        <FormGroup>
          <Label>Alert Name <span className="text-danger">*</span></Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Bullish breakouts with confirmation"
            invalid={name.trim() === ''}
          />
          {name.trim() === '' && (
            <div className="invalid-feedback d-block">A name is required to save the alert.</div>
          )}
        </FormGroup>

        {/* ── Similarity Matcher section ──────────────────────── */}
        <Card className={`mb-3 ${!hasFM ? 'opacity-50' : ''}`}>
          <CardHeader className="d-flex align-items-center gap-2 py-2" style={{ cursor: 'pointer' }} onClick={() => setHasFM((v) => !v)}>
            <Input type="checkbox" checked={hasFM} onChange={(e) => setHasFM(e.target.checked)} style={{ marginTop: 0 }} onClick={(e) => e.stopPropagation()} />
            <Label className="mb-0 fw-semibold" style={{ cursor: 'pointer' }}>Similarity Matcher</Label>
            <small className="text-muted ms-1">— our differentiator: how did similar price actions resolve?</small>
          </CardHeader>
          {hasFM && (
            <CardBody>
              <Row className="g-2 mb-2">
                <Col md={4}>
                  <Label className="small">Timeframe</Label>
                  <Input type="select" bsSize="sm" value={fmWindow} onChange={(e) => setFmWindow(e.target.value as WindowName)}>
                    {WINDOW_OPTIONS.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}
                  </Input>
                </Col>
                <Col md={4}>
                  <Label className="small">Direction</Label>
                  <div className="d-flex gap-1">
                    {(['up', 'down'] as const).map((d) => (
                      <Button key={d} size="sm" color={fmDirection === d ? 'primary' : 'outline-secondary'}
                        onClick={() => setFmDirection(d)}>
                        {d === 'up' ? '↑ Upside' : '↓ Downside'}
                      </Button>
                    ))}
                  </div>
                </Col>
                <Col md={4}>
                  <Label className="small" title={fmDirection === 'down' ? "Ratio of downside potential to upside risk. 1.0 = equal, 2.0 = 2× more downside." : "Ratio of upside potential to downside risk. 1.0 = equal, 2.0 = 2× more upside."}>
                    {fmDirection === 'down' ? 'Min downside/upside ratio' : 'Min upside/downside ratio'}
                  </Label>
                  <Input type="number" bsSize="sm" step="0.1" min="1.0" value={fmMinUoD}
                    onChange={(e) => setFmMinUoD(e.target.value)} placeholder="1.5" />
                  <small className="text-muted">1.0 = equal risk/reward, 1.5 = 50% more {fmDirection === 'down' ? 'downside' : 'upside'}</small>
                </Col>
              </Row>
              <div className="mt-2">
                <Button size="sm" color="link" className="p-0 text-muted small"
                  onClick={() => setFmAdvancedOpen((v) => !v)}>
                  {fmAdvancedOpen ? '▲ Hide advanced' : '▼ Advanced filters'}
                </Button>
              </div>
              {fmAdvancedOpen && (
                <Row className="g-2 mt-1">
                  <Col md={4}>
                    <Label className="small">{fmDirection === 'down' ? 'Min avg decline of matches (%)' : 'Min avg return of matches (%)'}</Label>
                    <Input type="number" bsSize="sm" step="0.5" value={fmMinAvgUp}
                      onChange={(e) => setFmMinAvgUp(e.target.value)} placeholder="e.g. 2.0" />
                  </Col>
                  <Col md={4}>
                    <Label className="small">Min % of matches that went {fmDirection === 'down' ? 'down' : 'up'}</Label>
                    <Input type="number" bsSize="sm" step="5" min="0" max="100" value={fmMinFrac}
                      onChange={(e) => setFmMinFrac(e.target.value)} placeholder="e.g. 60" />
                  </Col>
                  <Col md={4}>
                    <Label className="small">Min # of historical matches</Label>
                    <Input type="number" bsSize="sm" min="1" value={fmMinCount}
                      onChange={(e) => setFmMinCount(e.target.value)} placeholder="e.g. 5" />
                  </Col>
                </Row>
              )}
            </CardBody>
          )}
        </Card>

        {/* ── Classic Pattern section ─────────────────────────── */}
        <Card className={`mb-3 ${!hasCP ? 'opacity-50' : ''}`}>
          <CardHeader className="d-flex align-items-center gap-2 py-2" style={{ cursor: 'pointer' }} onClick={() => setHasCP((v) => !v)}>
            <Input type="checkbox" checked={hasCP} onChange={(e) => setHasCP(e.target.checked)} style={{ marginTop: 0 }} onClick={(e) => e.stopPropagation()} />
            <Label className="mb-0 fw-semibold" style={{ cursor: 'pointer' }}>Classic Pattern</Label>
            <small className="text-muted ms-1">— DTW-detected technical patterns (broad-stroke)</small>
          </CardHeader>
          {hasCP && (
            <CardBody>
              <FormGroup>
                <Label className="small">Pattern Types <span className="text-muted">(leave empty for all)</span></Label>
                <div className="mb-1"><small className="text-muted fw-bold">Bullish</small></div>
                <div className="d-flex flex-wrap gap-1 mb-2">
                  {bullishPatterns.map((p) => (
                    <Button key={p.value} size="sm"
                      color={selectedPatternTypes.has(p.value) ? 'success' : 'outline-secondary'}
                      onClick={() => togglePatternType(p.value)}>
                      {p.label}
                    </Button>
                  ))}
                </div>
                <div className="mb-1"><small className="text-muted fw-bold">Bearish</small></div>
                <div className="d-flex flex-wrap gap-1">
                  {bearishPatterns.map((p) => (
                    <Button key={p.value} size="sm"
                      color={selectedPatternTypes.has(p.value) ? 'danger' : 'outline-secondary'}
                      onClick={() => togglePatternType(p.value)}>
                      {p.label}
                    </Button>
                  ))}
                </div>
              </FormGroup>
              <FormGroup className="mt-2">
                <Label className="small">Timeframes <span className="text-muted">(leave empty for all)</span></Label>
                <div className="d-flex flex-wrap gap-1">
                  {PATTERN_WINDOW_OPTIONS.map((opt) => (
                    <Button key={opt.value} size="sm"
                      color={cpWindows.has(opt.value) ? 'primary' : 'outline-secondary'}
                      onClick={() => toggleCpWindow(opt.value)}>
                      {opt.label}
                    </Button>
                  ))}
                </div>
              </FormGroup>
              <Row className="g-2 mt-1">
                {selectedPatternTypes.size === 0 && (
                  <Col md={4}>
                    <Label className="small">Sentiment</Label>
                    <div className="d-flex gap-1">
                      {(['all', 'bullish', 'bearish'] as const).map((s) => (
                        <Button key={s} size="sm"
                          color={cpSentiment === s ? 'primary' : 'outline-secondary'}
                          onClick={() => setCpSentiment(s)}>
                          {s.charAt(0).toUpperCase() + s.slice(1)}
                        </Button>
                      ))}
                    </div>
                    <small className="text-muted">Or select specific patterns above</small>
                  </Col>
                )}
              </Row>
            </CardBody>
          )}
        </Card>

        {/* ── Indicator Conditions section ────────────────────── */}
        <Card className={`mb-3 ${!hasInd ? 'opacity-50' : ''}`}>
          <CardHeader className="d-flex align-items-center gap-2 py-2" style={{ cursor: 'pointer' }} onClick={() => setHasInd((v) => !v)}>
            <Input type="checkbox" checked={hasInd} onChange={(e) => setHasInd(e.target.checked)} style={{ marginTop: 0 }} onClick={(e) => e.stopPropagation()} />
            <Label className="mb-0 fw-semibold" style={{ cursor: 'pointer' }}>Indicator Conditions</Label>
            <small className="text-muted ms-1">— RSI, MACD, Bollinger Bands, Stochastic at latest date</small>
          </CardHeader>
          {hasInd && (
            <CardBody>
              {indicators.map((f, i) => (
                <IndicatorFilterEditor key={i} filter={f}
                  onChange={(u) => { const next = [...indicators]; next[i] = u; setIndicators(next); }}
                  onRemove={() => setIndicators(indicators.filter((_, idx) => idx !== i))} />
              ))}
              <Button size="sm" color="outline-secondary"
                onClick={() => setIndicators([...indicators, { type: 'rsi' }])}>
                + Add Indicator
              </Button>
            </CardBody>
          )}
        </Card>

        {/* ── Momentum Rank section ───────────────────────────── */}
        <Card className={`mb-3 ${!hasMR ? 'opacity-50' : ''}`}>
          <CardHeader className="d-flex align-items-center gap-2 py-2" style={{ cursor: 'pointer' }} onClick={() => setHasMR((v) => !v)}>
            <Input type="checkbox" checked={hasMR} onChange={(e) => setHasMR(e.target.checked)} style={{ marginTop: 0 }} onClick={(e) => e.stopPropagation()} />
            <Label className="mb-0 fw-semibold" style={{ cursor: 'pointer' }}>Momentum Rank</Label>
            <small className="text-muted ms-1">— upside potential percentile rank across all stocks</small>
          </CardHeader>
          {hasMR && (
            <CardBody>
              <Row className="g-2">
                <Col md={6}>
                  <Label className="small" title="Percentile 0–100. 80 = top 20% by upside potential ratio.">
                    Min rank percentile
                  </Label>
                  <Input type="number" bsSize="sm" min="0" max="100" step="5" value={mrMinRank}
                    onChange={(e) => setMrMinRank(e.target.value)} placeholder="e.g. 80 (top 20%)" />
                  <small className="text-muted">0–100; higher = stronger upside potential</small>
                </Col>
                <Col md={6}>
                  <Label className="small" title="Positive = rank improved vs 2 weeks ago.">
                    Min rank improvement (vs 2w ago)
                  </Label>
                  <Input type="number" bsSize="sm" step="1" value={mrMinRankChg}
                    onChange={(e) => setMrMinRankChg(e.target.value)} placeholder="e.g. 5 (improving)" />
                  <small className="text-muted">Positive = gaining momentum</small>
                </Col>
              </Row>
            </CardBody>
          )}
        </Card>

        {!hasFM && !hasCP && !hasInd && !hasMR && (
          <Alert color="warning" className="small">Enable at least one section above.</Alert>
        )}

        <Row className="g-2">
          <Col md={6}>
            <FormGroup>
              <Label>Email Digest</Label>
              <Input type="select" value={frequency} onChange={(e) => setFrequency(e.target.value as 'daily' | 'weekly')}>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
              </Input>
              <small className="text-muted">Emailed when this alert matches new signals (weekly = Saturdays).</small>
            </FormGroup>
          </Col>
          <Col md={6} className="d-flex flex-column justify-content-end">
            <FormGroup check>
              <Input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
              <Label check>Active</Label>
            </FormGroup>
            <FormGroup check>
              <Input type="checkbox" checked={watchlistOnly} onChange={(e) => setWatchlistOnly(e.target.checked)} />
              <Label check>Watchlist tickers only</Label>
            </FormGroup>
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose}>Cancel</Button>
        <Button color="primary" onClick={handleSave} disabled={!canSave}>Save Alert</Button>
      </ModalFooter>
    </>
  );
}

// ─── Alert card ───────────────────────────────────────────────────────────────

function alertSectionBadge(alert: UserAlert): string {
  const parts: string[] = [];
  if (alert.filters.featured_match) parts.push('Matcher');
  if (alert.filters.classic_pattern) parts.push('Pattern');
  if (alert.filters.indicators?.length) parts.push('Indicators');
  if (alert.filters.momentum_rank) parts.push('Momentum');
  return parts.length ? parts.join(' + ') : 'No filters';
}

function AlertCard({ alert, onEdit, onToggle, onDelete }: {
  alert: UserAlert; onEdit: () => void; onToggle: () => void; onDelete: () => void;
}) {
  const { featured_match: fm, classic_pattern: cp, indicators: inds, momentum_rank: mr } = alert.filters;

  return (
    <Card className={`mb-3 ${!alert.active ? 'opacity-50' : ''}`}>
      <CardHeader className="d-flex justify-content-between align-items-center">
        <div>
          <strong>{alert.name}</strong>
          <Badge color="info" className="ms-2 small">{alertSectionBadge(alert)}</Badge>
          {!alert.active && <Badge color="secondary" className="ms-1">Paused</Badge>}
        </div>
        <div className="d-flex gap-1">
          <Button size="sm" color="outline-secondary" onClick={onToggle}>{alert.active ? 'Pause' : 'Resume'}</Button>
          <Button size="sm" color="outline-primary" onClick={onEdit}>Edit</Button>
          <Button size="sm" color="outline-danger" onClick={onDelete}>Delete</Button>
        </div>
      </CardHeader>
      <CardBody className="small text-muted">
        {fm && (
          <div className="mb-1">
            <strong>Matcher:</strong>{' '}
            {fm.window_name} {fm.direction ?? 'up'} — ratio ≥ {fm.min_up_over_down ?? 1.0}
            {fm.min_avg_up !== undefined && `, avg ${fm.direction === 'down' ? 'decline' : 'return'} ≥ ${fm.min_avg_up}%`}
            {fm.min_fraction_up !== undefined && `, ≥ ${(fm.min_fraction_up * 100).toFixed(0)}% ${fm.direction === 'down' ? 'down' : 'up'}`}
          </div>
        )}
        {cp && (
          <div>
            <strong>Pattern:</strong>{' '}
            {cp.pattern_types?.length ? cp.pattern_types.join(', ') : 'all types'}
            {cp.windows?.length ? ` · ${cp.windows.map(w => PATTERN_WINDOW_OPTIONS.find(o => o.value === w)?.label ?? w).join(', ')}` : ''}
            {cp.sentiment && ` (${cp.sentiment})`}
          </div>
        )}
        {inds?.length ? (
          <div>
            <strong>Indicators:</strong>{' '}
            {inds.length} condition{inds.length > 1 ? 's' : ''}
          </div>
        ) : null}
        {mr && (
          <div>
            <strong>Momentum:</strong>{' '}
            {[
              mr.min_upr_rank !== undefined ? `rank ≥ ${mr.min_upr_rank}th pct` : null,
              mr.min_upr_rank_chg !== undefined ? `chg ≥ ${mr.min_upr_rank_chg}` : null,
            ].filter(Boolean).join(', ') || 'any rank'}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

// ─── AlertManagerPanel (embeddable, no Container/login guard) ─────────────────

export function AlertManagerPanel({ autoOpen = false }: { autoOpen?: boolean }) {
  const dispatch = useDispatch();
  const alertsState = useSelector((state: RootReducerState) => state.alerts);
  const posthog = usePostHog();

  const [editingAlert, setEditingAlert] = useState<UserAlert | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    dispatch(getAlerts() as never);
  }, [dispatch]);

  useEffect(() => {
    if (autoOpen) {
      setEditingAlert(null);
      setIsCreating(true);
      setModalOpen(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { alerts, loading, error } = alertsState;

  const openCreate = () => { setEditingAlert(null); setIsCreating(true); setModalOpen(true); };
  const openEdit = (a: UserAlert) => { setEditingAlert(a); setIsCreating(false); setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); setEditingAlert(null); setIsCreating(false); };

  const handleSave = (data: Omit<UserAlert, 'id' | 'created_at' | 'updated_at'>) => {
    if (isCreating) {
      dispatch(createAlert(data) as never);
      posthog.capture('alert_created', {
        alert_name: data.name,
        has_featured_match: !!data.filters.featured_match,
        has_classic_pattern: !!data.filters.classic_pattern,
        has_indicators: (data.filters.indicators?.length ?? 0) > 0,
        has_momentum_rank: !!data.filters.momentum_rank,
        frequency: data.frequency,
      });
    } else if (editingAlert) {
      dispatch(updateAlert(editingAlert.id, data) as never);
      posthog.capture('alert_updated', {
        alert_id: editingAlert.id,
        alert_name: data.name,
      });
    }
    closeModal();
  };

  const handleToggle = (alert: UserAlert) => {
    dispatch(updateAlert(alert.id, { active: !alert.active }) as never);
    posthog.capture('alert_toggled', {
      alert_id: alert.id,
      alert_name: alert.name,
      new_state: alert.active ? 'paused' : 'resumed',
    });
  };

  const handleDelete = (alert: UserAlert) => {
    if (window.confirm(`Delete "${alert.name}"?`)) {
      dispatch(deleteAlert(alert.id) as never);
      posthog.capture('alert_deleted', { alert_id: alert.id, alert_name: alert.name });
    }
  };

  return (
    <>
      <Row className="mb-3 align-items-center">
        <Col>
          <small className="text-muted">Patterns matching your alerts appear in your feed.</small>
        </Col>
        <Col xs="auto">
          <Button color="primary" onClick={openCreate}>+ New Alert</Button>
        </Col>
      </Row>

      {error && <Alert color="danger">{error}</Alert>}
      {loading && <div className="text-center mt-4"><Spinner /></div>}

      {!loading && alerts.length === 0 && (
        <div className="text-center mt-5 mb-5">
          <p className="text-muted">No alerts yet.</p>
          <Button color="primary" onClick={openCreate}>Create First Alert</Button>
        </div>
      )}

      {!loading && alerts.map((alert) => (
        <AlertCard key={alert.id} alert={alert}
          onEdit={() => openEdit(alert)}
          onToggle={() => handleToggle(alert)}
          onDelete={() => handleDelete(alert)} />
      ))}

      <Modal isOpen={modalOpen} toggle={closeModal} size="lg">
        <ModalHeader toggle={closeModal}>
          {isCreating ? 'New Alert' : `Edit: ${editingAlert?.name}`}
        </ModalHeader>
        {modalOpen && (
          <AlertEditor alert={editingAlert} onSave={handleSave} onClose={closeModal} />
        )}
      </Modal>
    </>
  );
}
